import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, Plus, Minus, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  q: 'How does ducted air conditioning work?',
  a: 'The main unit is hidden within a suspended ceiling or roof space. Conditioned air is then distributed through concealed ductwork and delivered into the room via discreet grilles or diffusers.'
}, {
  q: 'Is it noisy?',
  a: 'No, ducted systems are among the quietest commercial options available. Because the fan coil unit is hidden in the ceiling void, operational noise is significantly muffled, making it ideal for quiet office environments.'
}, {
  q: 'How much ceiling void space is required?',
  a: 'Typically, you need a minimum of 300mm to 400mm of clearance above the suspended ceiling to accommodate the indoor unit and ductwork, though slimline units are available for tighter spaces.'
}, {
  q: 'Can a ducted system provide heating as well as cooling?',
  a: 'Yes, modern commercial ducted systems are heat pumps, meaning they provide highly efficient heating during winter and cooling during summer from the same system.'
}, {
  q: 'What is the maintenance requirement?',
  a: 'Like all commercial systems, they require regular maintenance (usually bi-annually). Engineers will need access hatches in the ceiling to reach the hidden units for filter cleaning and servicing.'
}];
export function Ducted() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2215">
      {/* 1. Hero Section */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2216">
        <div className="container mx-auto px-6" data-id="element-2217">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2218">
            <div className="lg:col-span-7" data-id="element-2219">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2220">
                Systems / Ducted
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-2221">
                Ducted air conditioning
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed mb-10" data-id="element-2222">
                Discreet, hidden air conditioning for commercial spaces. Enjoy
                powerful, even climate control without compromising your
                interior design.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2223">
                <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2224">
                  Book a site survey
                </Link>
                <Link to="/contact" className="bg-white hover:bg-primary-dark/5 text-primary-dark border border-primary-dark/10 px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2225">
                  Speak to our team
                </Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5" data-id="element-2226">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6
            }} className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg" data-id="element-2227">
                <img src="https://cdn.magicpatterns.com/uploads/74DBnnnPaDiHoNeUuZD5vA/ducted-air-conditoning-accutemp.png" alt="Ducted air conditioning in a modern office" className="w-full h-full object-cover" data-id="element-2228" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Best For */}
      <section className="bg-white py-16 border-b border-primary-dark/5" data-id="element-2229">
        <div className="container mx-auto px-6" data-id="element-2230">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2231">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary-dark/40 mb-6" data-id="element-2232">
              Best for
            </h2>
            <p className="text-xl md:text-2xl text-primary-dark/80 leading-relaxed font-medium" data-id="element-2233">
              Offices, retail fitouts, premium commercial interiors, listed
              buildings, and spaces where aesthetics and quiet operation matter
              most.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Key Benefits */}
      <section className="bg-[#E8F0F8]/50 py-24" data-id="element-2234">
        <div className="container mx-auto px-6" data-id="element-2235">
          <div className="max-w-4xl mx-auto" data-id="element-2236">
            <h2 className="text-3xl md:text-4xl font-serif mb-10 text-center" data-id="element-2237">
              Key benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-2238">
              {['Hidden from view (only grilles are visible)', 'Exceptionally quiet operation', 'Even temperature distribution across large areas', 'Zoned control possible for different areas', 'Integrates seamlessly with VRV/VRF systems', 'Long lifespan with proper maintenance'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-2239">
                  <CheckCircle className="w-6 h-6 text-accent shrink-0" data-id="element-2240" />
                  <span className="font-medium text-primary-dark/80" data-id="element-2241">
                    {benefit}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Is this the right fit? */}
      <section className="bg-white py-24" data-id="element-2242">
        <div className="container mx-auto px-6" data-id="element-2243">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2244">
            Is this the right fit?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2245">
            <div className="bg-[#E8F0F8] p-10 rounded-3xl border border-primary-dark/5" data-id="element-2246">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-success" data-id="element-2247">
                <CheckCircle className="w-7 h-7" data-id="element-2248" />
                Good fit for
              </h3>
              <ul className="space-y-4" data-id="element-2249">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2250">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2251" />
                  <span data-id="element-2252">Offices needing clean, uninterrupted aesthetics</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2253">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2254" />
                  <span data-id="element-2255">High-end retail stores and showrooms</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2256">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2257" />
                  <span data-id="element-2258">Restaurants and premium hospitality venues</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2259">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2260" />
                  <span data-id="element-2261">Hotels and luxury accommodation</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-primary-dark/10 shadow-sm" data-id="element-2262">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-error" data-id="element-2263">
                <XCircle className="w-7 h-7" data-id="element-2264" />
                May not be ideal for
              </h3>
              <ul className="space-y-4" data-id="element-2265">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2266">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2267" />
                  <span data-id="element-2268">Very small individual rooms</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2269">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2270" />
                  <span data-id="element-2271">Buildings with no suspended ceiling or roof void</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2272">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2273" />
                  <span data-id="element-2274">
                    Tight budgets where simpler wall-mounted systems work
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AccuTemp design approach */}
      <section className="bg-primary-dark text-white py-24" data-id="element-2275">
        <div className="container mx-auto px-6" data-id="element-2276">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2277">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-2278">
              The AccuTemp design approach
            </h2>
            <p className="text-lg text-white/80 leading-relaxed" data-id="element-2279">
              We don't just install systems; we engineer solutions. Our in-house
              commercial team helps specify the exact ducted setup you need
              based on your room layout, comfort requirements, airflow dynamics,
              heat load, usage patterns, and budget. This design-led expertise
              ensures your system operates efficiently, quietly, and reliably
              for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FAQs */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2280">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-2281">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2282">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-2283">
            {faqs.map((faq, i) => <div key={i} className="border border-primary-dark/10 rounded-2xl overflow-hidden bg-white" data-id="element-2284">
                <button onClick={() => setOpenFaqId(openFaqId === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary-light/30 transition-colors" data-id="element-2285">
                  <span className="font-medium text-primary-dark pr-8" data-id="element-2286">
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0 text-primary-dark/40" data-id="element-2287">
                    {openFaqId === i ? <Minus className="w-5 h-5" data-id="element-2288" /> : <Plus className="w-5 h-5" data-id="element-2289" />}
                  </span>
                </button>
                <AnimatePresence data-id="element-2290">
                  {openFaqId === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-2291">
                      <div className="px-6 pb-6 pt-2 text-primary-dark/70 leading-relaxed border-t border-primary-dark/5" data-id="element-2292">
                        <p data-id="element-2293">{faq.a}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Brochure / literature section */}
      <section className="bg-white py-24 border-b border-primary-dark/5" data-id="element-2294">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2295">
          <h2 className="text-2xl font-serif mb-8" data-id="element-2296">Technical literature</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-2297">
            <a href="https://accutemp.co.uk/products/Ducted-Medium%20Static-%20FBA-A9%20%20RXM.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2298">
              <FileText className="w-5 h-5 text-accent" data-id="element-2299" />
              Download brochure
            </a>
            <a href="https://accutemp.co.uk/products/Commerical%20Cat.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2300">
              <Download className="w-5 h-5 text-accent" data-id="element-2301" />
              View commercial catalogue
            </a>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA */}
      <section className="bg-[#D4DDE8] py-24 text-center" data-id="element-2302">
        <div className="container mx-auto px-6" data-id="element-2303">
          <div className="max-w-2xl mx-auto" data-id="element-2304">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-10" data-id="element-2305">
              Ready to explore ducted options?
            </h2>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-2306">
              <Link to="/contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2307">
                Book a site survey
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2308" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-3 bg-white hover:bg-white/80 text-primary-dark px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2309">
                Get expert advice
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}